import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const Prachar = () => {
    // --- Context & Navigation ---
    const { appId, userId, language } = useAppId();
    const location = useLocation();
    const navigate = useNavigate();

    // Safely get idcard and contact_no from the previous page's navigation state
    const idcard = location.state?.voter_id || location.state?.idcard || location.state?.idcard_no;
    const contactNo = location.state?.contact_no || location.state?.mobile_no;

    // --- State ---
    const [showPopup, setShowPopup] = useState(true);
    const [selectedType, setSelectedType] = useState(null);
    const [pracharData, setPracharData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            title: "Prachar",
            selectType: "Select Promotion Type",
            selectSubtitle: "Choose the type of promotion you want to log.",
            missingId: "Invalid access. Voter ID Card is missing.",
            loading: "Fetching promotion data & opening WhatsApp...",
            noDataMessage: "No promotion records found for the selected type.",
            cancel: "Cancel"
        },
        hi: {
            title: "प्रचार",
            selectType: "प्रचार का प्रकार चुनें",
            selectSubtitle: "वह प्रचार प्रकार चुनें जिसे आप लॉग करना चाहते हैं।",
            missingId: "अमान्य पहुँच। मतदाता पहचान पत्र गायब है।",
            loading: "प्रचार डेटा ला रहा है और WhatsApp खोल रहा है...",
            noDataMessage: "चयनित प्रकार के लिए कोई प्रचार रिकॉर्ड नहीं मिला।",
            cancel: "रद्द करें"
        },
        gu: {
            title: "પ્રચાર",
            selectType: "પ્રચારનો પ્રકાર પસંદ કરો",
            selectSubtitle: "તમે જે પ્રચાર પ્રકાર લોગ કરવા માંગો છો તે પસંદ કરો.",
            missingId: "અમાન્ય ઍક્સેસ. મતદાર ઓળખ કાર્ડ ખૂટે છે.",
            loading: "પ્રચાર ડેટા લાવી રહ્યું છે અને WhatsApp ખોલી રહ્યું છે...",
            noDataMessage: "પસંદ કરેલ પ્રકાર માટે કોઈ પ્રચાર રેકોર્ડ મળ્યા નથી.",
            cancel: "રદ કરો"
        }
    };

    const txt = t[lang] || t.en;

    // --- Prachar Options ---
    const pracharOptions = [
        // { id: 'image', icon: '📸', color: '#3b82f6', bg: '#eff6ff' , name: 'Image Promotion'},
        { id: 'Video', icon: '🎥', color: '#ef4444', bg: '#fef2f2' , name: 'Video Promotion'},
        // { id: 'Sleep', icon: '📄', color: '#10b981', bg: '#ecfdf5' , name: 'Slip Promotion'},
        // { id: 'Print', icon: '🖨️', color: '#f59e0b', bg: '#fffbeb' , name: 'Printer Photo Promotion'},
        // { id: 'Audio', icon: '🔊', color: '#8b5cf6', bg: '#f5f3ff' , name: 'Audio Promotion'},
        { id: 'Text', icon: '💬', color: '#0ea5e9', bg: '#e0f2fe' , name: 'Text Promotion'},
        // { id: 'Selfie', icon: '🤳', color: '#ec4899', bg: '#fce7f3' , name: 'Selfie Promotion'}
    ];

    // --- Missing ID Check ---
    useEffect(() => {
        if (!idcard) {
            setShowPopup(false);
            setError(txt.missingId);
        }
    }, [idcard, txt.missingId]);

    // --- Fetch API Logic ---
    const handleSelectType = async (type) => {
        setSelectedType(type);
        setShowPopup(false);
        setLoading(true);
        setError('');

        try {
            const response = await axios.post(`${BASE_URL}/api/dis_prachar_with_log`, {
                app_id: appId,
                user_id: userId,
                prachar_type: type,
                idcard: idcard
            });

            if (response.data.success && response.data.data) {
                const fetchedData = response.data.data.result || response.data.data || [];
                setPracharData(fetchedData);
                
                // --- AUTO SHARE TRIGGER ---
                if (fetchedData.length > 0) {
                    await triggerAutoWhatsAppShare(fetchedData);
                } else {
                    alert(txt.noDataMessage);
                    navigate(-1); // Navigate back if no data
                }
            } else {
                alert(txt.noDataMessage);
                navigate(-1); // Navigate back if API fails to find data
            }
        } catch (err) {
            console.error("Error fetching prachar log:", err);
            alert("Failed to fetch promotion data. Please try again.");
            navigate(-1); // Navigate back on error
        } finally {
            setLoading(false);
        }
    };

    // Helper to check if a value looks like an image URL
    const isImageField = (key, value) => {
        if (!value || typeof value !== 'string') return false;
        const lowerKey = key.toLowerCase();
        return (
            lowerKey.includes('photo') || 
            lowerKey.includes('image') || 
            lowerKey.includes('pic') ||
            value.match(/\.(jpeg|jpg|gif|png)$/i) != null
        );
    };

    // Helper to hide internal DB IDs from being shared
    const isHiddenField = (key) => {
        const hiddenKeys = ['id', 'user_id', 'app_id', 'prachar_id', 'voter_id'];
        return hiddenKeys.includes(key.toLowerCase());
    };

    // --- AUTO FORMAT AND SHARE RAW CONTENT TO WHATSAPP ---
    const triggerAutoWhatsAppShare = async (dataList) => {
        let textMessage = ``;
        let imageUrl = null;

        // Combine all fetched items into a single message ONLY using the raw values (no keys/labels)
        dataList.forEach((item) => {
            Object.entries(item).forEach(([key, value]) => {
                if (!isHiddenField(key) && value) {
                    if (isImageField(key, value)) {
                        if (!imageUrl) imageUrl = value; // Grab the first image to share natively
                    } else {
                        // Just append the raw content
                        textMessage += `${value}\n\n`;
                    }
                }
            });
        });

        textMessage = textMessage.trim();

        // 1. Attempt Native Share (for attaching the actual image file)
        if (imageUrl && navigator.canShare) {
            try {
                const response = await fetch(imageUrl);
                const blob = await response.blob();
                const extension = imageUrl.split('.').pop().split(/#|\?/)[0] || 'jpg';
                const file = new File([blob], `prachar_image.${extension}`, { type: blob.type });

                if (navigator.canShare({ files: [file] })) {
                    await navigator.share({
                        text: textMessage,
                        files: [file]
                    });
                    navigate(-1); // Navigate back immediately after sharing natively
                    return; 
                }
            } catch (error) {
                console.warn('Native share blocked due to async delay or CORS. Falling back to wa.me link.', error);
            }
        }

        // 2. Fallback to WhatsApp wa.me link
        if (imageUrl) {
            textMessage += `\n\n${imageUrl}`; // Append URL since file couldn't be attached
        }

        const encodedMessage = encodeURIComponent(textMessage);
        let whatsappUrl = `https://wa.me/?text=${encodedMessage}`; 
        
        if (contactNo) {
            let formattedMobile = String(contactNo).replace(/\D/g, '');
            if (formattedMobile.length === 10) formattedMobile = '91' + formattedMobile;
            whatsappUrl = `https://wa.me/${formattedMobile}?text=${encodedMessage}`;
        }
        
        // Use an invisible anchor tag to safely open the new tab without modifying the current tab's location
        const link = document.createElement('a');
        link.href = whatsappUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Navigate back to the previous page slightly after opening the new tab
        setTimeout(() => {
            navigate(-1);
        }, 300);
    };

    return (
        <div className="app-container p-4" style={{ minHeight: '100vh', background: '#f8fafc', paddingBottom: '80px' }}>
            <Header title={txt.title} />

            {/* --- ERROR STATE --- */}
            {error && !loading && (
                <div className="error-toast mt-4">
                    <span className="error-icon">!</span>
                    <span className="error-text">{error}</span>
                </div>
            )}

            {/* --- LOADING STATE --- */}
            {loading && (
                <div className="loader-wrapper mt-5">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loading}</p>
                </div>
            )}

            {/* --- POPUP MODAL (Selection Screen) --- */}
            {showPopup && idcard && (
                <div className="modal-overlay">
                    <div className="modal-content-box slide-up-element">
                        <div className="modal-header bg-white">
                            <div>
                                <h2>{txt.selectType}</h2>
                            </div>
                            <button className="close-btn-icon" onClick={() => navigate(-1)}>
                                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        
                        <div className="options-grid">
                            {pracharOptions.map((option) => (
                                <button 
                                    key={option.id} 
                                    className="option-card"
                                    onClick={() => handleSelectType(option.id)}
                                >
                                    <div className="option-icon-wrapper" style={{ backgroundColor: option.bg, color: option.color }}>
                                        {option.icon}
                                    </div>
                                    <span className="option-title">{option.name}</span>
                                    <div className="option-chevron">
                                        <svg width="20" height="20" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"></polyline></svg>
                                    </div>
                                </button>
                            ))}
                        </div>
                        
                        <div className="modal-footer mt-3">
                            <button className="cancel-btn-full" onClick={() => navigate(-1)}>
                                {txt.cancel}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                /* Toast & Loader */
                .error-toast { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 12px; }
                .error-icon { width: 24px; height: 24px; background: #ef4444; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; flex-shrink: 0; }
                .error-text { color: #991b1b; font-size: 14px; font-weight: 500; }
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 50vh; }
                .spinner { width: 44px; height: 44px; border: 4px solid #e2e8f0; border-top: 4px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                .loader-text { color: #64748b; font-weight: 600; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                /* --- MODAL STYLES --- */
                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(15, 23, 42, 0.75); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; backdrop-filter: blur(4px); }
                .modal-content-box { background: #ffffff; border-radius: 24px; width: 100%; max-width: 450px; max-height: 90vh; overflow-y: auto; padding: 24px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); display: flex; flex-direction: column; }
                .modal-content-box::-webkit-scrollbar { width: 6px; }
                .modal-content-box::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }

                .modal-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
                .modal-header h2 { margin: 0 0 4px 0; font-size: 22px; font-weight: 800; color: #0f172a; }
                .close-btn-icon { background: #f1f5f9; border: none; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #64748b; cursor: pointer; transition: 0.2s; flex-shrink: 0; }
                .close-btn-icon:hover { background: #e2e8f0; color: #0f172a; }

                .options-grid { display: flex; flex-direction: column; gap: 12px; }
                .option-card { display: flex; align-items: center; padding: 12px 16px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; cursor: pointer; transition: all 0.2s ease; text-align: left; width: 100%; }
                .option-card:hover { border-color: #cbd5e1; background: #f8fafc; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
                
                .option-icon-wrapper { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; margin-right: 16px; flex-shrink: 0; }
                .option-title { flex-grow: 1; font-size: 15px; font-weight: 700; color: #1e293b; }
                .option-chevron { transition: transform 0.2s; }
                .option-card:hover .option-chevron { transform: translateX(4px); }

                .cancel-btn-full { width: 100%; padding: 14px; background: #f1f5f9; color: #475569; border: none; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: 0.2s; }
                .cancel-btn-full:hover { background: #e2e8f0; color: #0f172a; }

                .slide-up-element { animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

                /* Responsive */
                @media (max-width: 480px) {
                    .modal-content-box { padding: 20px; }
                    .modal-header h2 { font-size: 20px; }
                    .option-card { padding: 10px 14px; }
                    .option-icon-wrapper { width: 40px; height: 40px; font-size: 18px; margin-right: 12px; }
                    .option-title { font-size: 14px; }
                }
            `}</style>
        </div>
    );
};

export default Prachar;